import Router from 'next/router';
import { memo, useCallback } from 'react';

import { useAirModal } from '@air/provider-modal';

import { useCanAnnotateCentralizedPublicClip } from '~/components/AnnotationsV2/hooks/useCanAnnotateCentralizedPublicClip';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import {
  InitialCommentInput,
  type InitialCommentInputSubmitArgs,
} from '~/components/DiscussionsV2/InitialCommentInput/InitialCommentInput';
import { PublicCommentAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicCommentAnonymousSignUpModal';
import { type SSOCallbackData } from '~/components/PublicSSOCallback/utils';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedBoardAncestorsSelector } from '~/store/centralizedBoard/selectors';
import { useGetDiscussionMentionsMembers } from '~/swr-hooks/discussions/useGetDiscussionMentionsMembers';
import { usePublicSubmitNewDiscussion } from '~/swr-hooks/discussions/usePublicSubmitNewDiscussion';
import { useAirSelector } from '~/utils/ReduxUtils';

export const PublicDiscussionsForm = memo(() => {
  const { data: account } = useAccountContext();
  const annotations = useAnnotationV2Store((state) => state.annotations);
  const setAnnotations = useAnnotationV2Store((state) => state.setAnnotations);
  const { canSeeClipAnnotation: canAnnotateClip } = useCanAnnotateCentralizedPublicClip();
  const { submitNewDiscussion } = usePublicSubmitNewDiscussion();
  const [showSignUpModal] = useAirModal(PublicCommentAnonymousSignUpModal);
  const boardId = useURLBoardIdSelector();
  const { workspaceId: clipWorkspaceId } = usePublicWorkspace();
  const ancestors = useAirSelector(centralizedBoardAncestorsSelector);
  const { mentionMembers: mentionsList, isLoadingMentions } = useGetDiscussionMentionsMembers({
    boardId,
    ancestors,
    workspaceId: clipWorkspaceId,
  });

  const handleNewDiscussionSubmit = useCallback(
    ({ comment, timestamp, endTimestamp }: InitialCommentInputSubmitArgs) => {
      Router.ready(() => {
        const resolvedTimestamp = timestamp ?? undefined;

        const submitDiscussion = () => {
          submitNewDiscussion({
            trackLocation: 'public-board',
            newComment: comment,
            timestamp: resolvedTimestamp,
            endTimestamp,
            annotations,
          });
          setAnnotations([]);
        };

        if (account) {
          submitDiscussion();
        } else {
          const ssoCallbackData: SSOCallbackData = {
            url: Router.asPath,
            action: {
              action: 'create-public-discussion',
              data: {
                newComment: comment,
                timestamp: resolvedTimestamp,
                endTimestamp,
                annotations,
                trackLocation: 'public-board',
              },
            },
          };

          showSignUpModal({
            onLoginSuccess: submitDiscussion,
            ssoCallbackData,
          });
        }
      });
    },
    [account, annotations, setAnnotations, showSignUpModal, submitNewDiscussion],
  );

  return (
    <InitialCommentInput
      canAnnotateClip={canAnnotateClip}
      onSubmit={handleNewDiscussionSubmit}
      placeholder="Share your thoughts…"
      isLoadingMentions={isLoadingMentions}
      mentionsList={mentionsList}
      shouldShowMentionsButton={false}
      hasInternalAvatar
    />
  );
});

PublicDiscussionsForm.displayName = 'PublicDiscussionsForm';
